import type { ObjectWorkspaceAnchor, ObjectWorkspaceDocumentsState } from '@heidi/agents';
import type { QueryPatientDocument } from '@heidi/patients/contracts';
import type { GetDocumentsResponse } from '@heidi/session/contracts';

type SessionDocument = GetDocumentsResponse['documents'][number];

interface ResolveDocumentsStateOptions<TDocument> {
  documents: readonly TDocument[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

function resolveDocumentsState<TDocument>(
  { documents, isLoading, isError }: ResolveDocumentsStateOptions<TDocument>,
  toAnchor: (document: TDocument) => ObjectWorkspaceAnchor | null
): ObjectWorkspaceDocumentsState {
  if (isLoading) return { status: 'loading' };
  if (isError) return { status: 'error' };
  return {
    status: 'ready',
    documents: (documents ?? []).flatMap((document) => toAnchor(document) ?? []),
  };
}

export function resolveSessionDocumentsState(
  options: ResolveDocumentsStateOptions<SessionDocument> & { untitledTitle: string }
): ObjectWorkspaceDocumentsState {
  return resolveDocumentsState(options, (document) =>
    document.deleted_at
      ? null
      : {
          id: document.id,
          kind: 'document',
          title: document.name?.trim() || options.untitledTitle,
          ...(document.created_at ? { occurredAt: document.created_at } : {}),
        }
  );
}

export function resolvePatientDocumentsState(
  options: ResolveDocumentsStateOptions<QueryPatientDocument>
): ObjectWorkspaceDocumentsState {
  // A failed upload has nothing to preview.
  return resolveDocumentsState(options, (document) =>
    document.failure_reason
      ? null
      : {
          id: document.id,
          kind: 'document',
          title: document.file_name,
          occurredAt: document.created_at,
        }
  );
}
